import client from "./client";

// -------------------- Types --------------------

export type InvitationState = "never_invited" | "pending" | "expired" | "active";

export interface InvitationStatus {
  total: number;
  invited: number;
  activated: number;
  pending: number;
  never_invited: number;
}

export interface InvitationRow {
  employee_id: string;
  user_id: string;
  email: string;
  state: InvitationState;
  invitation_sent_at: string | null;
  invitation_expires_at: string | null;
  account_activated_at: string | null;
}

export interface BulkInviteResult {
  sent: number;
  skipped: number;
  already_active: number;
  not_found: number;
  links: Record<string, string>;
}

export interface InvitationValidateResponse {
  email: string;
  org_name: string | null;
}

// -------------------- HR-admin surface --------------------

export async function inviteEmployee(
  employeeId: string
): Promise<{ employee_id: string; invitation_link: string }> {
  const res = await client.post("/employees/invite", { employee_id: employeeId });
  return res.data;
}

export async function inviteBulk(
  employeeIds: string[]
): Promise<BulkInviteResult> {
  const res = await client.post<BulkInviteResult>("/employees/invite/bulk", {
    employee_ids: employeeIds,
  });
  return res.data;
}

export async function inviteAllUninvited(): Promise<{
  sent: number;
  links: Record<string, string>;
}> {
  const res = await client.post("/employees/invite/all");
  return res.data;
}

export async function getInvitationStatus(): Promise<InvitationStatus> {
  const res = await client.get<InvitationStatus>("/employees/invite/status");
  return res.data;
}

export async function listInvitations(): Promise<InvitationRow[]> {
  const res = await client.get<InvitationRow[]>("/employees/invite/list");
  return res.data;
}

// -------------------- Public (employee sets password) --------------------

export async function validateInvitationToken(
  token: string
): Promise<InvitationValidateResponse> {
  const res = await client.get<InvitationValidateResponse>(
    "/auth/invitation/validate",
    { params: { token } }
  );
  return res.data;
}

/**
 * Consume an invitation token + set the password. Returns the same
 * ``TokenResponse`` shape as /auth/login so the SPA can auto-login
 * the user without bouncing through the sign-in page. The refresh
 * cookie is set by the server.
 */
export async function setupPassword(
  token: string,
  newPassword: string
): Promise<import("@/types/auth").TokenResponse> {
  const res = await client.post<import("@/types/auth").TokenResponse>(
    "/auth/setup-password",
    { token, new_password: newPassword },
  );
  return res.data;
}
